import { Button, Pill } from "@truevault/aquilo";
import React, { useCallback, useState } from "react";
import { useHistory } from "react-router";
import { TaskDto } from "../../../../common/service/server/dto/TaskDto";
import { Callout, CalloutVariant } from "../../Callout";
import { ExpandableTaskDescription } from "./ExpandableTaskDescription";

type TaskCardProps = {
  task: TaskDto;
  titleOnly?: boolean;
  allowComplete?: boolean;
  onComplete?: (task: TaskDto) => Promise<void>;
  completed?: boolean;
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export const TaskCard: React.FC<TaskCardProps> = ({
  task,
  titleOnly,
  allowComplete = true,
  onComplete,
  completed = false,
}) => {
  if (titleOnly) {
    return (
      <>
        <div className="d-flex align-items-center">
          <div>
            <div className="d-flex">
              <h6 className="mt-0 mb-0 text-weight-medium flex-grow flex-float text-neutral-800">
                {task.name}
              </h6>
            </div>
          </div>
        </div>
        <hr />
      </>
    );
  }

  return (
    <>
      <TaskCardContainer
        task={task}
        allowComplete={allowComplete}
        onComplete={onComplete}
        completed={completed}
      >
        {Boolean(task.description) && <ExpandableTaskDescription description={task.description} />}
      </TaskCardContainer>
      <hr />
    </>
  );
};

const TaskCardContainer = ({
  task,
  allowComplete,
  onComplete,
  children,
  completed,
}: {
  task: TaskDto;
  allowComplete?: boolean;
  onComplete?: (task: TaskDto) => Promise<void>;
  children?: React.ReactNode;
  completed?: boolean;
}) => {
  const history = useHistory();

  const canComplete =
    allowComplete && Boolean(onComplete) && task.completionType === "MANUAL" && !task.completedAt;

  const [completing, setCompleting] = useState(false);

  const handleComplete = useCallback(async () => {
    setCompleting(true);
    try {
      await onComplete(task);
    } finally {
      setCompleting(false);
    }
  }, [task, onComplete]);

  return (
    <div className="tw-boundary">
      <div className="p-[24px] pl-0 ">
        <div>
          <h6
            className="mt-0 mb-xs font-semibold text-sm flex-grow flex-float text-neutral-800"
            style={{ width: "75%" }}
          >
            {task.name}
            {completed && <Pill variant="completed">Completed</Pill>}
          </h6>
        </div>
        <div
          className="d-flex justify-content-between align-items-center text-xs text-neutral-700"
          style={{ justifyContent: "space-between" }}
        >
          <div style={{ width: "75%" }}>
            {completed && (
              <div className="mb-xs text-gray-500">
                <p>Completed on {formatDate(task.completedAt)}</p>
              </div>
            )}
            {children}
          </div>
          {!completed && (
            <div style={{ width: "200px" }}>
              {task.callToAction && task.callToActionUrl ? (
                <Button
                  color="tertiary"
                  onClick={() => history.push(task.callToActionUrl)}
                  width="full"
                >
                  {task.callToAction}
                </Button>
              ) : null}
              {canComplete && (
                <Button
                  color="tertiary"
                  disabled={completing}
                  onClick={handleComplete}
                  width="full"
                >
                  Mark this Complete
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const EmptyTodoTasks = () => {
  return (
    <div className="max-width-650">
      <Callout variant={CalloutVariant.Clear}>
        You are all caught up on your tasks. Nice work 🎉
      </Callout>
    </div>
  );
};
